import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import CustomScrollbars from "components/CustomScrollbars";

// Spinner arrows on number fields collide with the dialog's compact rows.
const NUMBER_INPUT_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
} as const;

interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  /** Keep the dialog windowed on phones (default: it goes full screen). */
  disableMobileFullScreen?: boolean;
}

export default function AppDialog({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "xs",
  showCloseIcon = false,
  disableMobileFullScreen = false,
}: AppDialogProps) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isPhone && !disableMobileFullScreen}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>{title}</span>
          {showCloseIcon && (
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <Divider />
      {/* The content scrolls through CustomScrollbars, like every other
          scrollable surface in the platform, so DialogContent itself must not
          take the overflow - it only frames the area. */}
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          minHeight: 0,
          overflow: "hidden",
          ...NUMBER_INPUT_SX,
        }}
      >
        <CustomScrollbars>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>{children}</Box>
        </CustomScrollbars>
      </DialogContent>
      {actions != null && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>{actions}</DialogActions>
        </>
      )}
    </Dialog>
  );
}
